import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Trash2, Mail, Send, X } from "lucide-react";
import { Section } from "./_shared";
import { useConfirm } from "@/components/ConfirmDialog";

/** Mirror of backend `admin_broadcast_email::_plaintext_to_html`. Keeps the
 * live-preview identical to what the recipient will actually receive: if the
 * admin already provided block-level HTML we pass it through, otherwise we
 * escape and turn `\n\n+` into paragraphs and single `\n` into `<br>`. */
const BLOCK_TAGS_RE = /<\s*(p|br|div|h[1-6]|ul|ol|li|blockquote|table|hr|a|img|strong|em|b|i|span)\b/i;
function plaintextToHtml(text) {
  if (BLOCK_TAGS_RE.test(text)) return text;
  const esc = (s) => s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  return text.trim().split(/\n{2,}/)
    .map((para) => `<p>${esc(para).replace(/\n/g, "<br>\n")}</p>`)
    .join("\n");
}

/** Mirror of backend personalization: substitute {{imie}} with sample name.
 * Used only in the live preview so the admin sees what the recipient gets. */
function personalizePreview(text, sampleName) {
  return (text || "").replaceAll("{{imie}}", sampleName || "Nauczycielu");
}

/**
 * UsersTab — admin can filter teachers by voivodeship / city / language /
 * invite_segment, send broadcast email to the current filter (or explicit
 * subset via checkboxes), and soft-delete individual accounts.
 */
export default function UsersTab() {
  const confirm = useConfirm();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ voivodeship: "all", city: "all",
                                          language: "all", segment: "all",
                                          beta: "all",
                                          search: "" });
  const [selected, setSelected] = useState(new Set());
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [previewName, setPreviewName] = useState("Anna");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await teacherApi.get("/admin/teachers");
      setTeachers(Array.isArray(data) ? data : (data.teachers || []));
    } catch { toast.error("Nie udało się pobrać listy nauczycieli"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Compute distinct facets for filter dropdowns
  const facets = useMemo(() => {
    const V = new Set(), C = new Set(), L = new Set(), S = new Set();
    teachers.forEach((t) => {
      if (t.voivodeship) V.add(t.voivodeship);
      if (t.city) C.add(t.city);
      L.add(t.preferences?.language || "pl");
      if (t.invite_segment) S.add(t.invite_segment);
    });
    return {
      voivodeships: [...V].sort(), cities: [...C].sort(),
      languages: [...L].sort(), segments: [...S].sort(),
    };
  }, [teachers]);

  const filtered = useMemo(() => teachers.filter((t) => {
    if (filter.voivodeship !== "all" && t.voivodeship !== filter.voivodeship) return false;
    if (filter.city !== "all" && t.city !== filter.city) return false;
    if (filter.language !== "all" && (t.preferences?.language || "pl") !== filter.language) return false;
    if (filter.segment !== "all" && t.invite_segment !== filter.segment) return false;
    if (filter.beta === "yes" && !t.beta_tester) return false;
    if (filter.beta === "no" && t.beta_tester) return false;
    if (filter.search) {
      const q = filter.search.toLowerCase();
      if (!(`${t.email} ${t.name || ""}`.toLowerCase().includes(q))) return false;
    }
    return true;
  }), [teachers, filter]);

  const toggleSel = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const removeUser = async (id, email) => {
    const ok = await confirm({
      title: "Usunąć konto nauczyciela?",
      description: `Konto ${email} zostanie usunięte. To działanie jest odwracalne tylko przez SQL.`,
      confirmText: "Usuń konto",
      cancelText: "Anuluj",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await teacherApi.delete(`/admin/teachers/${id}`);
      toast.success("Konto usunięte");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd usuwania");
    }
  };

  const sendBroadcast = async () => {
    if (subject.length < 3 || body.length < 20) {
      toast.error("Podaj temat (≥3 znaki) i treść (≥20 znaków)."); return;
    }
    setSending(true);
    try {
      // Priority: explicit selection > current filter
      const payload = { subject, body };
      if (selected.size > 0) {
        payload.filter = { teacher_ids: [...selected] };
      } else {
        payload.filter = {};
        if (filter.voivodeship !== "all") payload.filter.voivodeship = filter.voivodeship;
        if (filter.city !== "all") payload.filter.city = filter.city;
        if (filter.language !== "all") payload.filter.language = filter.language;
        if (filter.segment !== "all") payload.filter.invite_segment = filter.segment;
      }
      const { data } = await teacherApi.post("/admin/teachers/broadcast-email", payload);
      toast.success(`Wysłano do ${data.sent} z ${data.total_recipients}${data.failed ? ` (${data.failed} nieudanych)` : ""}`);
      setBroadcastOpen(false);
      setSubject(""); setBody(""); setSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd wysyłki");
    } finally { setSending(false); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ok = await confirm({
      title: `Usunąć ${selected.size} ${selected.size === 1 ? "konto" : "kont"}?`,
      description: "Wszystkie zaznaczone konta zostaną soft-usunięte (blocked=true, deleted_at=now). Konta admina są pomijane. Operacja odwracalna tylko przez SQL.",
      confirmText: `Usuń ${selected.size}`,
      variant: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await teacherApi.post("/admin/teachers/bulk-delete", {
        teacher_ids: [...selected],
      });
      toast.success(`Usunięto: ${data.deleted}${data.skipped ? ` · Pominięto adminów: ${data.skipped}` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd bulk delete");
    }
  };

  return (
    <Section icon={Users} title="Użytkownicy">
      <p className="text-sm text-[#64748B] mb-4">
        Filtruj nauczycieli, wysyłaj indywidualnie lub jako broadcast, usuwaj konta.
        Newsletter to broadcast bez filtrów.
      </p>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-3" data-testid="users-filters">
        <Input placeholder="Szukaj (email/imię)" value={filter.search}
               onChange={(e) => setFilter({ ...filter, search: e.target.value })}
               className="brut-input border-2 border-[#0F172A] rounded-md h-10"
               data-testid="users-filter-search" />
        {[
          ["voivodeship", "województwo"],
          ["city", "miasto"],
          ["language", "język"],
          ["segment", "segment"],
        ].map(([k, label]) => (
          <Select key={k} value={filter[k]} onValueChange={(v) => setFilter({ ...filter, [k]: v })}>
            <SelectTrigger className="brut border-2 border-[#0F172A] rounded-md h-10 font-bold" data-testid={`users-filter-${k}`}>
              <SelectValue placeholder={label} />
            </SelectTrigger>
            <SelectContent className="brut bg-white border-2 border-[#0F172A]">
              <SelectItem value="all">{label} — wszystkie</SelectItem>
              {facets[k === "voivodeship" ? "voivodeships" : k === "city" ? "cities" : k === "language" ? "languages" : "segments"].map((v) => (
                <SelectItem key={v} value={v}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}
        <Select value={filter.beta} onValueChange={(v) => setFilter({ ...filter, beta: v })}>
          <SelectTrigger className="brut border-2 border-[#0F172A] rounded-md h-10 font-bold" data-testid="users-filter-beta">
            <SelectValue placeholder="Tester" />
          </SelectTrigger>
          <SelectContent className="brut bg-white border-2 border-[#0F172A]">
            <SelectItem value="all">Tester — wszyscy</SelectItem>
            <SelectItem value="yes">🧪 Tylko testerzy beta</SelectItem>
            <SelectItem value="no">Bez testerów</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <div className="text-sm">
          Wyniki: <b>{filtered.length}</b> z {teachers.length} · Zaznaczonych: <b>{selected.size}</b>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selected.size > 0 && (
            <Button
              size="sm" onClick={bulkDelete}
              className="brut brut-hover bg-[#FCA5A5] border-2 border-[#0F172A] font-bold rounded-md"
              data-testid="users-bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" strokeWidth={2.5} />
              Usuń zaznaczone ({selected.size})
            </Button>
          )}
          <Button
            size="sm" onClick={() => setBroadcastOpen(true)}
            disabled={filtered.length === 0}
            className="brut brut-hover bg-[#FEF08A] border-2 border-[#0F172A] font-bold rounded-md"
            data-testid="users-broadcast-btn"
          >
            <Mail className="w-4 h-4 mr-2" strokeWidth={2.5} />
            {selected.size > 0
              ? `Wyślij do zaznaczonych (${selected.size})`
              : `Broadcast do filtra (${filtered.length})`}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="brut border-2 border-[#0F172A] rounded-md bg-white overflow-x-auto max-h-[600px]" data-testid="users-list">
        {loading ? (
          <div className="p-4 text-sm text-[#64748B]">Ładowanie...</div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-sm text-[#64748B] italic">Brak wyników.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F1F5F9] border-b-2 border-[#0F172A]">
              <tr>
                <th className="p-2 w-8"><input type="checkbox"
                  checked={filtered.length > 0 && filtered.every((t) => selected.has(t.id))}
                  onChange={(e) => {
                    if (e.target.checked) setSelected(new Set(filtered.map((t) => t.id)));
                    else setSelected(new Set());
                  }}
                  data-testid="users-select-all" /></th>
                <th className="p-2 text-left">Email / Imię</th>
                <th className="p-2 text-left">Woj. / Miasto</th>
                <th className="p-2 text-center">Jęz.</th>
                <th className="p-2 text-left">Segment</th>
                <th className="p-2 text-center" title="Liczba utworzonych kartkówek (sesji)">Kartkówki</th>
                <th className="p-2 text-center">Status</th>
                <th className="p-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 500).map((t) => (
                <tr key={t.id} className="border-b border-[#E2E8F0]"
                    data-testid={`users-row-${t.id}`}>
                  <td className="p-2"><input type="checkbox"
                    checked={selected.has(t.id)} onChange={() => toggleSel(t.id)} /></td>
                  <td className="p-2">
                    <div className="font-bold break-all flex items-center gap-2 flex-wrap">
                      <span>{t.email}</span>
                      {t.beta_tester && (
                        <span
                          className="brut bg-[#DDD6FE] text-[10px] tracking-wide font-black uppercase px-1.5 py-0.5 rounded"
                          title="Konto utworzone w fazie beta"
                          data-testid={`users-tester-badge-${t.id}`}
                        >
                          🧪 Tester
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#64748B]">{t.name || "—"}</div>
                  </td>
                  <td className="p-2">
                    <div>{t.voivodeship || "—"}</div>
                    <div className="text-xs text-[#64748B]">{t.city || "—"}</div>
                  </td>
                  <td className="p-2 text-center text-xs font-bold">
                    {t.preferences?.language || "pl"}
                  </td>
                  <td className="p-2 text-xs">{t.invite_segment || "organic"}</td>
                  <td className="p-2 text-center" data-testid={`users-sessions-count-${t.id}`}>
                    <span
                      className={`brut font-black text-xs px-2 py-0.5 rounded inline-block min-w-[2rem] ${
                        (t.sessions_count ?? 0) === 0
                          ? "bg-[#F1F5F9] text-[#94A3B8]"
                          : "bg-[#FEF08A] text-[#0F172A]"
                      }`}
                      title={`${t.sessions_count ?? 0} utworzonych kartkówek`}
                    >
                      {t.sessions_count ?? 0}
                    </span>
                  </td>
                  <td className="p-2 text-center text-xs">
                    {t.email_verified ? "✅" : "⏳"} {t.role === "admin" ? "🛡️" : ""} {t.blocked ? "🚫" : ""}
                  </td>
                  <td className="p-2 text-center">
                    <a href={`mailto:${t.email}`} className="brut brut-hover inline-block border-2 border-[#0F172A] rounded p-1 bg-white mr-1"
                       title="Wyślij email indywidualny" data-testid={`users-mailto-${t.id}`}>
                      <Mail className="w-3 h-3" strokeWidth={2.5} />
                    </a>
                    <button onClick={() => removeUser(t.id, t.email)}
                            disabled={t.role === "admin"}
                            className="brut brut-hover inline-block border-2 border-[#0F172A] rounded p-1 bg-[#FCA5A5] disabled:opacity-30"
                            title="Usuń konto" data-testid={`users-delete-${t.id}`}>
                      <Trash2 className="w-3 h-3" strokeWidth={2.5} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Broadcast modal */}
      {broadcastOpen && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
             onClick={() => !sending && setBroadcastOpen(false)}
             data-testid="broadcast-modal">
          <div className="brut border-4 border-[#0F172A] bg-white rounded-md max-w-5xl w-full p-6 max-h-[92vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="font-black text-xl">Broadcast email</h2>
                <p className="text-xs text-[#64748B]">
                  Odbiorcy: <b>{selected.size > 0 ? selected.size : filtered.length}</b>
                  {selected.size === 0 && " (przefiltrowana lista)"}
                </p>
              </div>
              <button onClick={() => setBroadcastOpen(false)} disabled={sending}
                      className="brut brut-hover border-2 border-[#0F172A] rounded p-1 bg-white"
                      data-testid="broadcast-close">
                <X className="w-5 h-5" strokeWidth={2.5} />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Left: editor */}
              <div className="space-y-3">
                <div>
                  <Label className="font-bold text-sm">Temat</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                         maxLength={200} placeholder="np. Ważna aktualizacja aplikacji"
                         className="brut-input border-2 border-[#0F172A] rounded-md h-11 mt-1"
                         data-testid="broadcast-subject" />
                </div>
                <div>
                  <Label className="font-bold text-sm">Treść</Label>
                  <Textarea value={body} onChange={(e) => setBody(e.target.value)}
                            rows={12} maxLength={10000}
                            placeholder={"Wpisz treść tak jak w mailu — nowe linie i akapity będą zachowane.\n\nCześć {{imie}}, chciałbym Ci powiedzieć że...\n\nHTML też jest OK, jeśli chcesz formatowanie."}
                            className="brut-input border-2 border-[#0F172A] rounded-md text-sm mt-1"
                            data-testid="broadcast-body" />
                  <div className="text-xs text-[#64748B] mt-1 flex items-center justify-between">
                    <span>Bez auto-powitania — wysyłamy dokładnie to, co tu wpiszesz.</span>
                    <span>{body.length}/10000</span>
                  </div>
                  {/* Personalization hint */}
                  <div className="mt-2 border-2 border-[#0F172A] bg-[#FEF08A] rounded-md
                                  px-3 py-2 text-[11px] leading-relaxed"
                       data-testid="broadcast-vars-hint">
                    <div className="font-black uppercase tracking-[0.12em] text-[10px] mb-1">
                      Zmienne (opcjonalne)
                    </div>
                    <div>
                      Wstaw{" "}
                      <button type="button"
                              onClick={() => setBody((b) => b + (b.endsWith(" ") || !b ? "" : " ") + "{{imie}}")}
                              className="font-mono font-black bg-white border-2 border-[#0F172A]
                                         px-1.5 py-0.5 rounded hover:-translate-y-[1px] transition-transform"
                              data-testid="broadcast-insert-imie">
                        {"{{imie}}"}
                      </button>{" "}
                      w treści — podstawi imię nauczyciela. Gdy pole puste w bazie → <b>„Nauczycielu"</b>.
                      Pomiń zmienną, jeśli chcesz wysłać mail bezosobowy.
                    </div>
                  </div>
                </div>
              </div>
              {/* Right: live preview */}
              <div className="space-y-2" data-testid="broadcast-preview">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label className="font-bold text-sm inline-flex items-center gap-2">
                    Podgląd w skrzynce
                    <span className="brut bg-[#FEF08A] text-[10px] px-1.5 py-0.5 rounded font-black uppercase tracking-wide">
                      Live
                    </span>
                  </Label>
                  {/* Preview-name selector: only visible when body uses {{imie}} */}
                  {body.includes("{{imie}}") && (
                    <div className="flex items-center gap-1.5 text-[11px]"
                         data-testid="broadcast-preview-name-wrap">
                      <span className="text-[#64748B] font-bold">Podgląd dla:</span>
                      <Input value={previewName}
                             onChange={(e) => setPreviewName(e.target.value)}
                             maxLength={40}
                             placeholder="Anna"
                             className="brut-input border-2 border-[#0F172A] rounded h-7 w-24 text-[11px] px-2"
                             data-testid="broadcast-preview-name" />
                    </div>
                  )}
                </div>
                <div className="brut border-2 border-[#0F172A] rounded-md bg-[#F8FAFC] overflow-hidden">
                  {/* Inbox header */}
                  <div className="bg-white border-b-2 border-[#0F172A] px-3 py-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="text-[#64748B]">Od:</span>
                      <b>Kartkówki Chemia &lt;chemiainfo@kartkowka.pl&gt;</b>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[#64748B]">Temat:</span>
                      <b className="truncate">{subject || <span className="text-[#94A3B8] font-normal italic">(brak tematu)</span>}</b>
                    </div>
                  </div>
                  {/* Body preview */}
                  <div className="p-4 bg-white min-h-[280px] text-sm text-[#0F172A]"
                       data-testid="broadcast-preview-body">
                    {body.trim()
                      ? <div dangerouslySetInnerHTML={{ __html: plaintextToHtml(personalizePreview(body, previewName)) }} />
                      : <div className="text-[#94A3B8] italic">Zacznij pisać treść — podgląd pojawi się tutaj.</div>
                    }
                    <hr style={{border:"none",borderTop:"1px solid #E2E8F0",margin:"24px 0"}} />
                    <div className="text-xs text-[#64748B]">
                      Wiadomość od zespołu Kartkówki Chemia<br />
                      <span className="text-[#64748B]">chemiainfo@kartkowka.pl</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <Button onClick={() => setBroadcastOpen(false)} disabled={sending}
                      className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md">
                Anuluj
              </Button>
              <Button onClick={sendBroadcast} disabled={sending || !subject || !body}
                      className="brut brut-hover bg-[#0F172A] text-white border-2 border-[#0F172A] font-bold rounded-md"
                      data-testid="broadcast-send">
                <Send className={`w-4 h-4 mr-2 ${sending ? "animate-pulse" : ""}`} strokeWidth={2.5} />
                {sending ? "Wysyłam..." : "Wyślij"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}
